import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { COLORS, TYPOGRAPHY } from '../../lib/styleGuide';
import { TickSquare, InfoCircle, TimeCircle } from 'react-iconly';
import { getEnvironmentConfig } from '../../lib/environment';

const SUCCESS_GREEN = '#10B981';
const WARNING_ORANGE = '#F59E0B';
const ERROR_RED = '#F44336';

export default function VerifyIdentityReturn() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const [verificationStatus, setVerificationStatus] = useState('checking');
  const [error, setError] = useState('');
  const { apiBaseUrl } = getEnvironmentConfig();

  useEffect(() => {
    if (!currentUser?.uid) {
      navigate('/onboarding');
      return;
    }

    // Check verification status after returning from Stripe
    checkVerificationStatus();
  }, [currentUser, navigate]);

  const checkVerificationStatus = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/getIdentityStatus?userId=${currentUser.uid}`);
      if (response.ok) {
        const data = await response.json();
        setVerificationStatus(data.status);
        
        // Handle different verification statuses
        if (data.status === 'verified') {
          // Verification successful - redirect to success page
          navigate('/onboarding/success');
        } else if (data.status === 'requires_input') {
          // Verification needs more input - redirect back to verification
          navigate('/onboarding/verify-identity');
        }
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
      setError('Failed to check verification status');
    }
  };

  const continueToNextStep = () => {
    navigate('/onboarding/success');
  };

  const retryVerification = () => {
    navigate('/onboarding/verify-identity');
  };

  const renderContent = () => {
    switch (verificationStatus) {
      case 'verified':
        return (
          <div style={{ textAlign: 'left', padding: '40px 0' }}>
            <h1 style={{ 
              fontSize: '32px', 
              color: SUCCESS_GREEN, 
              margin: '0 0 16px 0',
              fontWeight: 700
            }}>
              Verification Complete!
            </h1>
            <p style={{ 
              fontSize: '18px', 
              color: '#666', 
              margin: '0 0 32px 0',
              lineHeight: 1.6,
              maxWidth: 500
            }}>
              Congratulations! Your identity has been successfully verified. You're now ready to start selling on Vesta.
            </p>
            <button
              onClick={continueToNextStep}
              style={{
                background: COLORS.PRIMARY_GRADIENT,
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                padding: '16px 40px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                minHeight: 56,
                minWidth: 200,
                boxShadow: '0 4px 12px #0002',
                transition: 'all 0.2s',
              }}
            >
              Continue
            </button>
          </div>
        );

      case 'pending':
        return (
          <div style={{ textAlign: 'left', padding: '40px 0' }}>
            <h1 style={{ 
              fontSize: '32px', 
              color: WARNING_ORANGE, 
              margin: '0 0 16px 0',
              fontWeight: 700
            }}>
              Verification in Progress
            </h1>
            <p style={{ 
              fontSize: '14px', 
              color: '#666', 
              margin: '0 0 32px 0',
              lineHeight: 1.6,
              maxWidth: 500
            }}>
              Your documents are being reviewed by our verification team. This usually takes a few minutes to a few hours. We'll notify you once the verification is complete.
            </p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
              <button
                onClick={() => navigate('/onboarding/success')}
                style={{
                  background: COLORS.PRIMARY_GRADIENT,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  minHeight: 48,
                  minWidth: 120,
                  boxShadow: '0 4px 12px #0002',
                  transition: 'all 0.2s',
                }}
              >
                NEXT
              </button>
            </div>
          </div>
        );

      case 'requires_input':
        return (
          <div style={{ textAlign: 'left', padding: '40px 0' }}>
            <h1 style={{ 
              fontSize: '32px', 
              color: WARNING_ORANGE, 
              margin: '0 0 16px 0',
              fontWeight: 700
            }}>
              Additional Information Needed
            </h1>
            <p style={{ 
              fontSize: '18px', 
              color: '#666', 
              margin: '0 0 32px 0',
              lineHeight: 1.6,
              maxWidth: 500
            }}>
              We need more information to complete your verification. Please review the requirements and try again with complete documentation.
            </p>
            <button
              onClick={retryVerification}
              style={{
                background: COLORS.PRIMARY_GRADIENT,
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                padding: '16px 40px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                minHeight: 56,
                minWidth: 200,
                boxShadow: '0 4px 12px #0002',
                transition: 'all 0.2s',
              }}
            >
              Try Again
            </button>
          </div>
        );

      case 'failed':
        return (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <InfoCircle
              set="bold"
              size={80}
              primaryColor={ERROR_RED}
            />
            <h1 style={{ 
              fontSize: '32px', 
              color: ERROR_RED, 
              margin: '24px 0 16px 0',
              fontWeight: 700
            }}>
              Verification Failed
            </h1>
            <p style={{ 
              fontSize: '18px', 
              color: '#666', 
              margin: '0 0 32px 0',
              lineHeight: 1.6,
              maxWidth: 500,
              marginLeft: 'auto',
              marginRight: 'auto'
            }}>
              We couldn't verify your identity with the provided documents. Please ensure you're using valid, unexpired government-issued identification and try again.
            </p>
            <button
              onClick={retryVerification}
              style={{
                background: COLORS.PRIMARY_GRADIENT,
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                padding: '16px 40px',
                fontSize: '18px',
                fontWeight: 600,
                cursor: 'pointer',
                minHeight: 56,
                minWidth: 200,
                boxShadow: '0 4px 12px #0002',
                transition: 'all 0.2s',
              }}
            >
              Try Again
            </button>
          </div>
        );

      case 'checking':
        return (
          <div style={{ textAlign: 'left', padding: '40px 0' }}>
            <div style={{
              width: 80,
              height: 80,
              border: '4px solid #f3f3f3',
              borderTop: '4px solid COLORS.PRIMARY_GRADIENT_START',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 0 24px 0'
            }} />
            <h1 style={{ 
              fontSize: '32px', 
              color: '#222', 
              margin: '0 0 16px 0',
              fontWeight: 700
            }}>
              Checking Verification Status
            </h1>
            <p style={{ 
              fontSize: '14px', 
              color: '#666', 
              margin: 0,
              lineHeight: 1.6
            }}>
              Please wait while we check your verification status...
            </p>
          </div>
        );

      default:
        return (
          <div style={{ textAlign: 'left', padding: '40px 0' }}>
            <h1 style={{ 
              fontSize: '32px', 
              color: ERROR_RED, 
              margin: '0 0 16px 0',
              fontWeight: 700
            }}>
              Verification Error
            </h1>
            <p style={{ 
              fontSize: '14px', 
              color: '#666', 
              margin: '0 0 32px 0',
              lineHeight: 1.6,
              maxWidth: 500
            }}>
              {error || 'An error occurred while checking your verification status. Please try again or contact support.'}
            </p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
              <button
                onClick={() => navigate('/onboarding/success')}
                style={{
                  background: COLORS.PRIMARY_GRADIENT,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  minHeight: 48,
                  minWidth: 120,
                  boxShadow: '0 4px 12px #0002',
                  transition: 'all 0.2s',
                }}
              >
                NEXT
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column', 
      justifyContent: 'center', 
      alignItems: 'center', 
      background: '#FAFAFA',
      padding: '20px'
    }}>
      <div style={{ 
        width: '100%', 
        maxWidth: 700, 
        background: '#fff',
        borderRadius: 20,
        boxShadow: '0 8px 32px #0001',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{ 
          background: COLORS.PRIMARY_GRADIENT, 
          padding: '32px',
          textAlign: 'left'
        }}>
          <h2 style={{ 
            fontWeight: 700, 
            fontSize: '28px', 
            margin: 0, 
            color: '#fff' 
          }}>
            Identity Verification Status
          </h2>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.9)', 
            fontSize: '14px', 
            margin: '8px 0 0 0',
            lineHeight: 1.5
          }}>
            Welcome back from verification
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: '32px' }}>
          {renderContent()}
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `
      }} />
    </div>
  );
}
